import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { X } from "lucide-react";
import { http, API } from "../lib/api";

const AdsCtx = createContext([]);
const AGE_KEY = "bpv_18_plus_confirmed";

export const AdsProvider = ({ children }) => {
  const [ads, setAds] = useState([]);
  useEffect(() => {
    http.get("/ads").then((r) => setAds(Array.isArray(r.data) ? r.data : [])).catch(() => setAds([]));
    if (window.location.pathname.startsWith("/admin")) return;
    http.get("/site-code").then((r) => {
      if (r.data?.head_code) injectCode(r.data.head_code, document.head);
      if (r.data?.body_code) injectCode(r.data.body_code, document.body);
    }).catch(() => {});
  }, []);
  return <AdsCtx.Provider value={ads}>{children}</AdsCtx.Provider>;
};

export const useAds = (placement) => {
  const ads = useContext(AdsCtx);
  return useMemo(() => ads.filter((a) => a.placement === placement), [ads, placement]);
};

const track = (id, ev) => http.post(`/ads/${id}/${ev}`).catch(() => {});

const CodeFrame = ({ ad }) => (
  <iframe
    title={ad.name}
    srcDoc={`<!doctype html><html><head><base target="_blank"><style>html,body{margin:0;padding:0;background:transparent;overflow:hidden;text-align:center}</style></head><body>${ad.code}</body></html>`}
    width={ad.width}
    height={ad.height}
    scrolling="no"
    className="max-w-full border-0"
    sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-same-origin allow-top-navigation-by-user-activation"
  />
);

export const AdSlot = ({ placement, className = "" }) => {
  const list = useAds(placement);
  const [pick] = useState(() => Math.random());
  const ad = list.length ? list[Math.floor(pick * list.length)] : null;
  const seen = useRef("");

  useEffect(() => {
    if (!ad || seen.current === ad.id) return;
    seen.current = ad.id;
    track(ad.id, "impression");
  }, [ad]);

  if (!ad) return null;
  return (
    <div className={`flex flex-col items-center ${className}`} data-testid={`ad-slot-${placement}`}>
      <span className="font-mono-d mb-2 text-[10px] uppercase tracking-[0.3em] text-slate-400">Publicidade</span>
      {ad.mode === "image" ? (
        <a href={ad.link_url || "#"} target="_blank" rel="sponsored noopener noreferrer" onClick={() => track(ad.id, "click")} data-testid={`ad-link-${ad.id}`} className="block max-w-full">
          <img src={`${API}/ads/${ad.id}/image?v=${encodeURIComponent(ad.updated_at || "")}`} alt={ad.name} style={{ maxWidth: ad.width }} className="h-auto w-full object-contain" />
        </a>
      ) : (
        <CodeFrame ad={ad} />
      )}
    </div>
  );
};

export const MobileSticky = () => {
  const [closed, setClosed] = useState(() => sessionStorage.getItem("bpv_sticky_closed") === "1");
  const has = useAds("mobile_sticky").length > 0;
  if (!has || closed) return null;
  const close = () => {
    sessionStorage.setItem("bpv_sticky_closed", "1");
    setClosed(true);
  };
  return (
    <>
      <div className="h-[84px] md:hidden" aria-hidden />
      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-rose-900/40 bg-[#08080a]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-xl md:hidden" data-testid="mobile-sticky-ad">
        <button onClick={close} aria-label="Fechar anúncio" data-testid="mobile-sticky-close" className="absolute -top-7 right-2 flex h-7 w-7 items-center justify-center border border-rose-900/40 border-b-0 bg-[#08080a] text-slate-300">
          <X size={14} />
        </button>
        <AdSlot placement="mobile_sticky" className="py-1 [&>span]:mb-1 [&>span]:text-[9px]" />
      </div>
    </>
  );
};

const due = (ad) => {
  const last = Number(localStorage.getItem(`bpv_pop_${ad.id}`) || 0);
  return Date.now() - last > (ad.frequency_hours || 24) * 3600 * 1000;
};
const mark = (ad) => localStorage.setItem(`bpv_pop_${ad.id}`, String(Date.now()));

const injectCode = (code, target = document.body) => {
  const holder = document.createElement("div");
  holder.innerHTML = code;
  Array.from(holder.childNodes).forEach((node) => {
    if (node.tagName === "SCRIPT") {
      const s = document.createElement("script");
      Array.from(node.attributes).forEach((a) => s.setAttribute(a.name, a.value));
      s.text = node.text;
      target.appendChild(s);
    } else {
      target.appendChild(node);
    }
  });
};

export const Popunders = () => {
  const pops = useAds("popunder");

  useEffect(() => {
    if (!pops.length) return undefined;
    const ready = () => localStorage.getItem(AGE_KEY) === "1";
    const codeAds = pops.filter((a) => a.mode === "code" && a.code);
    const urlAds = pops.filter((a) => a.mode === "url" && a.popunder_url);
    let injected = false;
    const runCode = () => {
      if (injected || !ready()) return;
      injected = true;
      codeAds.filter(due).forEach((a) => {
        mark(a);
        track(a.id, "impression");
        injectCode(a.code);
      });
    };
    const onClick = (e) => {
      if (!ready() || e.target.closest?.("[data-testid='age-gate-modal']")) return;
      runCode();
      const ad = urlAds.find(due);
      if (!ad) return;
      mark(ad);
      track(ad.id, "impression");
      const w = window.open(ad.popunder_url, "_blank", "noopener");
      if (w) window.focus();
    };
    runCode();
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, [pops]);

  return null;
};
